import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Star, Loader2, CheckCircle2, AlertTriangle, PlugZap, Ruler, ExternalLink, Wand2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getCurrentAccount } from '../../lib/auth';
import { Button } from '../ui/button';

const fmt = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const deriveLists = (props) => {
  const ready = [], needs = [], connected = [];
  (props || []).forEach((p) => {
    const r = p.readiness || {};
    const row = { site_uid: p.site_uid, name: p.name, score_eligibility: r.score_eligibility,
      reasons: r.reasons || [], gfa: r.gfa, primary_function: r.primary_function };
    if (r.status === 'ready') ready.push(row);
    else if (r.status === 'connected') connected.push(row);
    else if (['missing_details', 'missing_meters', 'needs_attention', 'unsupported'].includes(r.status)) needs.push(row);
  });
  return { ready, needs, connected };
};

/**
 * ENERGY STAR setup card rendered inside the agent chat. Refresh-safe:
 * re-hydrates live readiness from the backend on mount, falling back to the
 * snapshot Astrid packaged into the card.
 */
export const EspmSetupCard = ({ card }) => {
  const orgId = card?.org_id || getCurrentAccount()?.id || null;
  const environment = card?.environment || 'test';
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const load = useCallback(() => {
    if (!orgId) { setLoading(false); return; }
    setLoading(true);
    const to = new Date(); const from = new Date(); from.setFullYear(from.getFullYear() - 3);
    const iso = (d) => d.toISOString().slice(0, 10);
    api.get('/energy-star/mappings', { params: { account_id: orgId, environment, date_from: iso(from), date_to: iso(to) } })
      .then(({ data }) => setLive(data))
      .catch(() => setLive(null))
      .finally(() => setLoading(false));
  }, [orgId, environment]);

  useEffect(() => { load(); }, [load]);

  const { ready, needs, connected, counts, connectedFlag } = useMemo(() => {
    if (live) {
      const lists = deriveLists(live.properties);
      return { ...lists, counts: live.readiness_counts || {}, connectedFlag: !!live.connected };
    }
    return {
      ready: card?.ready || [], needs: card?.needs || [], connected: card?.connected_props || [],
      counts: card?.counts || {}, connectedFlag: !!card?.connected,
    };
  }, [live, card]);

  const envLabel = environment === 'test' ? 'Sandbox' : 'Production';
  const settingsUrl = `/settings?tab=energy-star${orgId ? `&account_id=${orgId}` : ''}`;
  const reportUrl = `/bills/energy-star${orgId ? `?account_id=${orgId}` : ''}`;

  const connectReady = async () => {
    const site_uids = ready.map((r) => r.site_uid);
    if (!site_uids.length || !orgId) return;
    setConnecting(true);
    try {
      const { data } = await api.post('/energy-star/auto-create',
        { environment, site_uids, push_after_create: true, push_months: 12 },
        { params: { account_id: orgId } });
      const pushed = data.push?.records_pushed || 0;
      if (data.errors > 0) toast.warning(`Connected with ${data.errors} issue(s) — see the setup page.`);
      else toast.success(`Connected ${data.properties_created} propert(ies) + ${data.meters_created} meter(s); pushed ${fmt(pushed)} month-read(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connect failed');
    } finally { setConnecting(false); }
  };

  const Chip = ({ tone, icon: Icon, label, n }) => (
    <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
      <Icon className="h-3 w-3" /> {n} {label}
    </span>
  );

  return (
    <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50/40 p-3" data-testid="espm-setup-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-800">
          <Star className="h-4 w-4 text-amber-500" /> ENERGY STAR setup
        </div>
        <span className="rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-600" data-testid="espm-card-env">{envLabel}</span>
      </div>

      {/* Connection + readiness summary */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="espm-card-summary">
        {connectedFlag ? (
          <Chip tone="bg-emerald-50 text-emerald-700 border-emerald-200" icon={CheckCircle2} label="Connected acct" n="" />
        ) : (
          <Chip tone="bg-rose-50 text-rose-700 border-rose-200" icon={AlertTriangle} label="Not connected" n="" />
        )}
        <Chip tone="bg-sky-50 text-sky-700 border-sky-200" icon={PlugZap} label="connected" n={connected.length} />
        <Chip tone="bg-emerald-50 text-emerald-700 border-emerald-200" icon={CheckCircle2} label="ready" n={ready.length} />
        <Chip tone="bg-amber-50 text-amber-700 border-amber-200" icon={AlertTriangle} label="need info" n={needs.length} />
      </div>

      {loading && (
        <div className="mt-2 flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking readiness…</div>
      )}

      {/* Not connected → guide to Settings */}
      {!connectedFlag && (
        <div className="mt-2 rounded-lg border border-amber-200 bg-white p-2 text-xs text-slate-600" data-testid="espm-card-connect-hint">
          Add your Portfolio Manager username &amp; password once on the Energy Star setup page, then I’ll connect every ready property for you.
          <a href={settingsUrl} className="mt-1.5 inline-flex items-center gap-1 font-semibold text-amber-700 hover:underline" data-testid="espm-card-open-settings">
            Open Energy Star setup <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}

      {/* Ready list + connect action */}
      {ready.length > 0 && (
        <div className="mt-2" data-testid="espm-card-ready">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Ready to connect</div>
          <ul className="mt-1 space-y-0.5">
            {ready.slice(0, 5).map((p) => (
              <li key={p.site_uid} className="flex items-center gap-1.5 text-xs text-slate-700">
                <Building2 className="h-3 w-3 text-slate-400" /> {p.name}
                {p.gfa ? <span className="text-slate-400 flex items-center gap-0.5"><Ruler className="h-2.5 w-2.5" />{fmt(p.gfa)}</span> : null}
              </li>
            ))}
            {ready.length > 5 && <li className="text-[11px] text-slate-400">+{ready.length - 5} more</li>}
          </ul>
          {connectedFlag && (
            <Button size="sm" className="mt-2 h-8 rounded-md bg-amber-600 hover:bg-amber-700 text-xs" onClick={connectReady} disabled={connecting} data-testid="espm-card-connect-all">
              {connecting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5 mr-1.5" />} Connect {ready.length} &amp; sync 12 months
            </Button>
          )}
        </div>
      )}

      {/* Needs-info list */}
      {needs.length > 0 && (
        <div className="mt-2" data-testid="espm-card-needs">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Need information</div>
          <ul className="mt-1 space-y-0.5">
            {needs.slice(0, 5).map((p) => (
              <li key={p.site_uid} className="text-xs text-slate-700">
                <span className="font-medium">{p.name}</span>
                {(p.reasons || []).length ? <span className="text-amber-700"> — add {p.reasons.join(', ')}</span> : null}
              </li>
            ))}
            {needs.length > 5 && <li className="text-[11px] text-slate-400">+{needs.length - 5} more</li>}
          </ul>
          <div className="mt-1 text-[11px] text-slate-500">Tell me the gross floor area for any of these and I’ll fill it in.</div>
        </div>
      )}

      <a href={reportUrl} className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-700 hover:underline" data-testid="espm-card-open-report">
        View Energy Star report <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
};

export default EspmSetupCard;
